import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Stack,
  Typography
} from '@mui/material';
import {
  AccountBalanceOutlined,
  ExpandLess,
  ExpandMore,
  PeopleOutlined,
  QueryStatsOutlined,
  TrendingUpOutlined,
  WorkOutlined
} from '@mui/icons-material';
import { mainColors } from '../../theme/themeConfig';

interface IdeaItem {
  id: string;
  prompt: string;
  subtitle: string;
  title: string;
}

interface IdeaGroup {
  color: string;
  icon: React.ReactNode;
  id: string;
  ideas: IdeaItem[];
  label: string;
  tag: string;
}

const IDEA_GROUPS: IdeaGroup[] = [
  {
    color: '#1E88E5',
    icon: <AccountBalanceOutlined sx={{ color: '#1E88E5', fontSize: 18 }} />,
    id: 'financial',
    ideas: [
      {
        id: 'monthly-revenue',
        prompt:
          'Show me monthly revenue collected (paid invoices only) for the last 12 months as an area chart.',
        subtitle: 'Cash in by month, last 12 months',
        title: 'Monthly Revenue Collected'
      },
      {
        id: 'ar-aging',
        prompt:
          'Show me my AR aging breakdown — group unpaid invoices into buckets: 0-30 days, 31-60, 61-90, and 90+ days overdue. Show total dollar amount and count per bucket.',
        subtitle: 'Unpaid invoices by days overdue',
        title: 'AR Aging Breakdown'
      },
      {
        id: 'outstanding-ar',
        prompt:
          'What is my total outstanding AR right now? Break it down by invoice status (Pending vs Overdue) and show the dollar totals.',
        subtitle: 'Total unpaid balance by status',
        title: 'Outstanding AR Total'
      },
      {
        id: 'avg-invoice',
        prompt:
          'Show me the average invoice value per month for the last 12 months as a line chart.',
        subtitle: 'Avg invoice amount trend',
        title: 'Average Invoice Value Trend'
      }
    ],
    label: 'Financial Performance',
    tag: 'Finance'
  },
  {
    color: '#43A047',
    icon: <TrendingUpOutlined sx={{ color: '#43A047', fontSize: 18 }} />,
    id: 'sales',
    ideas: [
      {
        id: 'win-rate',
        prompt:
          'What is my proposal win rate this year? Show approved proposals as a percentage of total sent, and compare month by month.',
        subtitle: 'Approved / total proposals %',
        title: 'Proposal Win Rate'
      },
      {
        id: 'pipeline-value',
        prompt:
          'Show me my current sales pipeline value broken down by proposal status (Awaiting, Updated, In Negotiation). Show total dollar value per stage.',
        subtitle: 'Dollar value at each pipeline stage',
        title: 'Pipeline Value by Stage'
      },
      {
        id: 'lead-conversion',
        prompt:
          'Show me my lead to proposal conversion rate by month for the last 6 months — how many leads converted vs total leads created.',
        subtitle: 'Leads converted to proposals monthly',
        title: 'Lead-to-Proposal Conversion'
      },
      {
        id: 'proposal-losses',
        prompt:
          'Show me my lost and rejected proposals this year. Break down by status and show the count per month.',
        subtitle: 'Lost + rejected proposals by month',
        title: 'Proposal Losses Breakdown'
      }
    ],
    label: 'Sales Pipeline',
    tag: 'Sales'
  },
  {
    color: '#E53935',
    icon: <WorkOutlined sx={{ color: '#E53935', fontSize: 18 }} />,
    id: 'operations',
    ideas: [
      {
        id: 'completion-rate',
        prompt:
          'Show me my job completion rate for the last 6 months — completed projects as a percentage of total scheduled. Show the trend by month.',
        subtitle: 'Completed jobs % by month',
        title: 'Job Completion Rate'
      },
      {
        id: 'visits-per-week',
        prompt:
          'Show me the number of scheduled visits (calendar events) per week for the last 8 weeks as a bar chart.',
        subtitle: 'Scheduled visits per week',
        title: 'Visits Per Week'
      },
      {
        id: 'rescheduled-trend',
        prompt:
          'Show me the trend of rescheduled and cancelled projects by month for the last 6 months.',
        subtitle: 'Rescheduled + cancelled by month',
        title: 'Rescheduled & Cancelled Trend'
      },
      {
        id: 'unfilled-assignments',
        prompt:
          'Show me unfilled assignment slots by week for the last 4 weeks — calendar events where required assignments exceeds current assignments.',
        subtitle: 'Jobs with unfilled technician slots',
        title: 'Unfilled Assignments'
      }
    ],
    label: 'Field Operations',
    tag: 'Operations'
  },
  {
    color: '#8E24AA',
    icon: <PeopleOutlined sx={{ color: '#8E24AA', fontSize: 18 }} />,
    id: 'clients',
    ideas: [
      {
        id: 'revenue-by-client',
        prompt:
          'Show me revenue by client for this year — top 10 clients by total paid invoice amount. Show as a bar chart.',
        subtitle: 'Top 10 clients by revenue this year',
        title: 'Revenue by Client (Top 10)'
      },
      {
        id: 'new-clients',
        prompt:
          'Show me new clients added per month for the last 12 months as an area chart.',
        subtitle: 'Client acquisition rate by month',
        title: 'Client Acquisition Rate'
      },
      {
        id: 'active-projects-client',
        prompt:
          'Which clients have the most active projects right now? Show top 10 by active project count.',
        subtitle: 'Active project count per client',
        title: 'Active Projects by Client'
      }
    ],
    label: 'Client Health',
    tag: 'Clients'
  },
  {
    color: '#FF6F00',
    icon: <QueryStatsOutlined sx={{ color: '#FF6F00', fontSize: 18 }} />,
    id: 'workforce',
    ideas: [
      {
        id: 'labor-variance',
        prompt:
          'Analyze my labor variance — show actual hours worked vs scheduled hours by month for the last 6 months. Highlight months where actual exceeded scheduled.',
        subtitle: 'Actual vs scheduled hours monthly',
        title: 'Labor Variance Trend'
      },
      {
        id: 'technician-jobs',
        prompt:
          'Show me the number of jobs completed per technician this month. Rank them from most to least active.',
        subtitle: 'Jobs per technician this month',
        title: 'Jobs Per Technician'
      },
      {
        id: 'overtime-trend',
        prompt:
          'Show me overtime hours trend by month for the last 6 months — hours worked beyond what was scheduled.',
        subtitle: 'Excess hours worked vs scheduled',
        title: 'Overtime Hours Trend'
      }
    ],
    label: 'Workforce & Labor',
    tag: 'Workforce'
  }
];

interface AbeIdeasMenuProps {
  onSelectIdea: (prompt: string) => void;
}

function AbeIdeasMenu({ onSelectIdea }: AbeIdeasMenuProps): JSX.Element {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    financial: true
  });

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 1.5 }}>
      <Typography
        sx={{
          color: 'text.secondary',
          display: 'block',
          fontWeight: 600,
          letterSpacing: '0.08em',
          mb: 1,
          textTransform: 'uppercase'
        }}
        variant="caption"
      >
        Ask Abe — Pre-Built Analyses
      </Typography>
      <Typography
        sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 2 }}
        variant="caption"
      >
        Select any analysis — Abe runs it against your live data and generates a
        chart you can save to any dashboard.
      </Typography>

      <Stack spacing={1}>
        {IDEA_GROUPS.map((group) => (
          <Box key={group.id}>
            {/* Group header */}
            <Box
              onClick={() => toggleGroup(group.id)}
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                alignItems: 'center',
                borderRadius: 1,
                cursor: 'pointer',
                display: 'flex',
                gap: 1,
                px: 1,
                py: 0.75
              }}
            >
              {group.icon}
              <Typography sx={{ flex: 1, fontWeight: 600 }} variant="body2">
                {group.label}
              </Typography>
              <Chip
                label={group.ideas.length}
                size="small"
                sx={{
                  bgcolor: `${group.color}15`,
                  color: group.color,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  height: 18
                }}
              />
              {openGroups[group.id] ? (
                <ExpandLess sx={{ color: 'text.disabled', fontSize: 16 }} />
              ) : (
                <ExpandMore sx={{ color: 'text.disabled', fontSize: 16 }} />
              )}
            </Box>

            {/* Ideas */}
            <Collapse in={!!openGroups[group.id]}>
              <Stack spacing={0.75} sx={{ ml: 1, mt: 0.5 }}>
                {group.ideas.map((idea) => (
                  <Card
                    key={idea.id}
                    sx={{
                      '&:hover': {
                        borderColor: group.color,
                        boxShadow: `0 0 0 1px ${group.color}30`
                      },
                      borderRadius: 1.5,
                      transition: 'all 0.15s'
                    }}
                    variant="outlined"
                  >
                    <CardActionArea onClick={() => onSelectIdea(idea.prompt)}>
                      <CardContent
                        sx={{ '&:last-child': { pb: 1.25 }, p: 1.25 }}
                      >
                        <Typography
                          sx={{
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 0.25
                          }}
                          variant="caption"
                        >
                          {idea.title}
                        </Typography>
                        <Typography
                          sx={{ color: 'text.disabled', display: 'block' }}
                          variant="caption"
                        >
                          {idea.subtitle}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Collapse>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default AbeIdeasMenu;
